'use client';

import { Delete } from '@mui/icons-material';
import { Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { GridActionsCellItem, GridApi, GridColDef } from '@mui/x-data-grid-premium';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { adaptGridValueCallback } from '@parkchirp/ui-library';
import type { Dayjs } from 'dayjs';
import React, { MutableRefObject } from 'react';

import { CountAndRate } from '../../../types/leases';
import { dayjs, getAllowRowEdits, getGridRows } from '../../../utils';
import { isOwnerPortal, isTenantPortal } from '../../../utils/constants';
import { RateAutoComplete } from './rateAutoComplete';

type GetTableButtonsInput = {
	newMode: boolean;
	leaseNewMode: boolean;
	editMode: boolean;
	setEditMode: (x: boolean) => void;
	allowEdit: boolean;
	allowDelete: boolean;
	saveInProgress: boolean;
	deleteInProgress?: boolean;
	handleSave: () => void;
	handleAddRow: () => void;
	handleCancel: () => void;
	handleDelete?: () => void;
};

export function getTableButtons({
	newMode,
	leaseNewMode,
	editMode,
	setEditMode,
	allowEdit,
	allowDelete,
	saveInProgress,
	deleteInProgress,
	handleSave,
	handleAddRow,
	handleCancel,
	handleDelete,
}: GetTableButtonsInput) {
	if (!allowEdit || isTenantPortal || isOwnerPortal) return null;

	if (leaseNewMode) {
		return (
			<Box display='flex' justifyContent='start' mt='1em' ml='1em'>
				<Button variant='contained' onClick={handleAddRow}>
					Add Row
				</Button>
			</Box>
		);
	}

	if (newMode || editMode) {
		return (
			<Box display='flex' justifyContent='space-between' mt='1em' ml='1em' mr='1em'>
				<Button variant='contained' onClick={handleAddRow}>
					Add Row
				</Button>
				<Box display='flex' gap='1em'>
					<Button variant='contained' disabled={saveInProgress} onClick={handleCancel}>
						Cancel
					</Button>
					{saveInProgress ? (
						<CircularProgress />
					) : (
						<Button
							variant='contained'
							style={{ minWidth: '6em', justifyContent: 'center' }}
							onClick={handleSave}
						>
							Save
						</Button>
					)}
				</Box>
			</Box>
		);
	}

	return (
		<Box display='flex' justifyContent='end' gap='1em' mt='1em' mr='1em'>
			{allowDelete &&
				(deleteInProgress ? (
					<CircularProgress color='error' />
				) : (
					<Button variant='outlined' color='error' onClick={handleDelete}>
						Delete
					</Button>
				))}
			<Button variant='contained' onClick={() => setEditMode(true)}>
				Edit
			</Button>
		</Box>
	);
}

type GetRateAndSublotFieldsInput = {
	rateId: string | null;
	setRateId: (x: string) => void;
	sublotId: number | null;
	setSublotId: (x: number) => void;
	rateName: string;
	sublotName: string;
	rateOptions: any[];
	sublotOptions: { id: number; name: string }[];
	newMode: boolean;
	gridApiRef: MutableRefObject<GridApi>;
	grossRatePrice: string;
};

export function getRateAndSublotFields({
	rateId,
	setRateId,
	sublotId,
	setSublotId,
	rateName,
	sublotName,
	rateOptions,
	sublotOptions,
	newMode,
	gridApiRef,
	grossRatePrice,
}: GetRateAndSublotFieldsInput) {
	if (newMode) {
		return (
			<Box display='flex' gap='3em' width='55%'>
				<RateAutoComplete
					id='rate-name'
					rateKey='rateId'
					label='Rate'
					formData={{
						rateId,
						leasingCountAndRateHistory: getGridRows(gridApiRef),
					}}
					rates={rateOptions}
					handleInputChangeLeasingRight={({ target: { value } }) => setRateId(value)}
					disabled={!rateOptions.length}
					InputProps={{ notched: false, sx: { backgroundColor: 'white' } }}
					InputLabelProps={{
						shrink: true,
						sx: {
							position: 'relative',
							color: 'black',
							fontWeight: 'bolder',
							fontSize: 'larger',
							display: 'flex',
							justifyContent: 'center',
							top: '4px',
						},
					}}
					data-cy='rate-name-select'
				/>
				<FormControl fullWidth>
					<InputLabel
						id='sublot-label'
						required
						shrink
						sx={{
							position: 'relative',
							color: 'black',
							fontWeight: 'bolder',
							fontSize: 'larger',
							display: 'flex',
							justifyContent: 'center',
							top: '4px',
						}}
					>
						Sublot
					</InputLabel>
					<Select
						label='Sublot'
						labelId='sublot-label'
						id='sublot'
						name='sublotId'
						value={sublotId?.toString() ?? ''}
						onChange={({ target: { value } }) => setSublotId(Number(value))}
						autoComplete='off'
						notched={false}
						sx={{ backgroundColor: 'white' }}
						data-cy='sublot-select'
					>
						{!sublotOptions?.length ? (
							<MenuItem key='no-sublots'>No sublots configured for this facility</MenuItem>
						) : (
							sublotOptions.map((sublot) => (
								<MenuItem key={`sublot-${sublot.id.toString()}`} value={sublot.id.toString()}>
									{sublot.name}
								</MenuItem>
							))
						)}
					</Select>
				</FormControl>
			</Box>
		);
	}

	return (
		<Box display='flex' flexDirection='column' alignItems='start' gap='0.5em' ml='1em'>
			<Box display='flex' justifyContent='center'>
				<Typography fontWeight='bold' mr='1em'>
					Rate:
				</Typography>
				<Typography>{rateName}</Typography>
			</Box>
			<Box display='flex' justifyContent='center'>
				<Typography fontWeight='bold' mr='1em'>
					Gross Rate Price:
				</Typography>
				<Typography>${grossRatePrice}</Typography>
			</Box>
			<Box display='flex' justifyContent='center'>
				<Typography fontWeight='bold' mr='1em'>
					Sublot:
				</Typography>
				<Typography>{sublotName}</Typography>
			</Box>
		</Box>
	);
}

export const getLeaseCountAndRateColumns: (
	gridApiRef: MutableRefObject<GridApi>,
	countAndRateProp: CountAndRate,
	facilityTimezone: string,
	newMode: boolean,
	subscriptionHasInvoice: boolean,
) => GridColDef[] = (gridApiRef, countAndRateProp, facilityTimezone, newMode, subscriptionHasInvoice) => [
	{
		field: 'actions',
		type: 'actions' as const,
		getActions: ({ id, row }) => {
			const allowRowEdits = getAllowRowEdits(
				row,
				countAndRateProp.history,
				facilityTimezone,
				newMode,
				subscriptionHasInvoice,
			);

			if (!allowRowEdits) return [];

			if (getGridRows(gridApiRef).length < 2) return [];

			return [
				<GridActionsCellItem
					icon={<Delete />}
					onClick={() => gridApiRef.current.updateRows([{ id, _action: 'delete' }])}
					label=''
					key='delete'
				/>,
			];
		},
		flex: 0.5,
		cellClassName: 'MuiDataGrid-cell--actions',
	},
	{
		field: 'startDate',
		headerName: 'Start Date',
		headerAlign: 'center' as const,
		align: 'center' as const,
		sortable: false,
		editable: true,
		flex: 1,
		valueFormatter: adaptGridValueCallback(({ value }) => dayjs.utc(value).format('MM/DD/YYYY')),
		renderEditCell: ({ id, field, value }) => (
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<DatePicker<Dayjs>
					value={dayjs(value)}
					onChange={(newValue) => {
						const formattedValue = newValue.format('YYYY-MM-DD');
						gridApiRef.current.setEditCellValue({ id, field, value: formattedValue });
					}}
					slotProps={{ openPickerIcon: { 'data-cy': `start-date-picker` } }}
					sx={{
						'& .MuiOutlinedInput-input': { fontSize: 'small' },
						'& .MuiOutlinedInput-notchedOutline': {
							border: 'none',
						},
					}}
				/>
			</LocalizationProvider>
		),
	},
	{
		field: 'endDate',
		headerName: 'End Date',
		headerAlign: 'center' as const,
		align: 'center' as const,
		sortable: false,
		editable: true,
		flex: 1,
		valueFormatter: adaptGridValueCallback(({ value }) => dayjs.utc(value).format('MM/DD/YYYY')),
		renderEditCell: ({ id, field, value }) => (
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<DatePicker<Dayjs>
					value={dayjs(value)}
					onChange={(newValue) => {
						const formattedValue = newValue.format('YYYY-MM-DD');
						gridApiRef.current.setEditCellValue({ id, field, value: formattedValue });
					}}
					slotProps={{ openPickerIcon: { 'data-cy': `end-date-picker` } }}
					sx={{
						'& .MuiOutlinedInput-input': { fontSize: 'small' },
						'& .MuiOutlinedInput-notchedOutline': {
							border: 'none',
						},
					}}
				/>
			</LocalizationProvider>
		),
	},
	{
		field: 'minCount',
		headerName: 'Min (Must Take)',
		headerAlign: 'center' as const,
		align: 'center' as const,
		sortable: false,
		editable: true,
		flex: 1,
		valueFormatter: adaptGridValueCallback(({ value }) => value ?? '-'),
		valueParser: (value) => Number((value ?? 0).replace(/\D/g, '')), // Remove all non-numbers,
		valueSetter: adaptGridValueCallback(({ value, row }) => ({ ...row, minCount: Number(value) })),
	},
	{
		field: 'maxCount',
		headerName: 'Max',
		headerAlign: 'center' as const,
		align: 'center' as const,
		sortable: false,
		editable: true,
		flex: 1,
		valueFormatter: adaptGridValueCallback(({ value }) => value || '-'),
		valueParser: (value) => Number((value ?? 0).replace(/\D/g, '')), // Remove all non-numbers,
		valueSetter: adaptGridValueCallback(({ value, row }) => ({
			...row,
			maxCount: Number(value),
			...(!value && { enforceMaxCount: false }),
		})),
	},
	{
		field: 'enforceMaxCount',
		headerName: 'Enforce Max?',
		type: 'singleSelect' as const,
		valueOptions: [
			{ value: true, label: 'Yes' },
			{ value: false, label: 'No' },
		],
		headerAlign: 'center' as const,
		align: 'center' as const,
		sortable: false,
		editable: true,
		flex: 1.5,
	},
	{
		field: 'rightToConvert',
		headerName: 'Right To Convert',
		type: 'singleSelect' as const,
		valueOptions: [
			{ value: true, label: 'Yes' },
			{ value: false, label: 'No' },
		],
		headerAlign: 'center' as const,
		align: 'center' as const,
		sortable: false,
		editable: true,
		flex: 1.5,
	},
];
